<template>
  <div class="track-dashboard">
    <div class="header">
      <h2>Рекомендации по отслеживаемым артефактам</h2>
      <Button
        label="Добавить артефакт"
        icon="pi pi-plus"
        @click="openAddDialog"
        class="p-button-success"
      />
    </div>

    <!-- Показываем загрузку если данных еще нет -->
    <div v-if="!artefactAllMap" class="loading-container">
      <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" />
      <p>Загрузка данных...</p>
    </div>

    <Toast />

    <DataTable
      v-if="artefactAllMap"
      :value="recommendations"
      :paginator="true"
      :rows="25"
      :rowsPerPageOptions="[10, 25, 50, 100]"
      currentPageReportTemplate="Показано {first} до {last} из {totalRecords} записей"
      emptyMessage="Нет рекомендаций"
    >
      <Column field="name" header="Артефакт">
        <template #body="{ data }">
          <div style="white-space: nowrap; position: relative">
            <span
              @click="copyToClipboard(getArtefactName(data.artefactId), 'name')"
              v-text="getArtefactName(data.artefactId)"
            ></span
            >&nbsp;
            <span style="white-space: nowrap" :style="`color: ${getColorQlt(data.quality)}`">
              {{ data.sharpening }}
            </span>
          </div>
        </template>
      </Column>

      <Column field="currentPrice" header="Текущая цена">
        <template #body="{ data }">
          <span @click="copyToClipboard(data.currentPrice - 1, 'price')" class="clickable-price">
            {{ formatPrice(data.currentPrice) }}
          </span>
        </template>
      </Column>

      <Column field="averagePrice" header="Средняя цена">
        <template #body="{ data }">
          <span @click="copyToClipboard(data.averagePrice - 1, 'price')" class="clickable-price">
            {{ formatPrice(Number(data.averagePrice)) }}
          </span>
        </template>
      </Column>

      <Column field="buyBelowPrice" header="Купить ниже">
        <template #body="{ data }">
          <span
            @click="copyToClipboard(data.buyBelowPrice - 1, 'price')"
            :class="getPriceClass(data.currentPrice, data.buyBelowPrice, 'buy')"
            class="clickable-price"
          >
            {{ formatPrice(data.buyBelowPrice) }}
          </span>
        </template>
      </Column>

      <Column field="sellAbovePrice" header="Продать выше">
        <template #body="{ data }">
          <span
            @click="copyToClipboard(data.sellAbovePrice - 1, 'price')"
            :class="getPriceClass(data.currentPrice, data.sellAbovePrice, 'sell')"
            class="clickable-price"
          >
            {{ formatPrice(data.sellAbovePrice) }}
          </span>
        </template>
      </Column>

      <Column field="recommendation" header="Рекомендация">
        <template #body="{ data }">
          <Tag :value="data.recommendation" :severity="data.recommendationSeverity" />
        </template>
      </Column>

      <Column field="currentElems" header="Лоты">
        <template #body="{ data }">
          <div class="lots-container">
            <template v-for="(lot, index) in data.currentElems" :key="index">
              <span
                class="lot-item"
                :class="{
                  'lot-to-buy': shouldBuyLot(lot, data),
                  'lot-normal': !shouldBuyLot(lot, data)
                }"
              >
                <span
                  @click="copyToClipboard(lot.price - 1, 'price')"
                  class="lot-price clickable-lot-price"
                >
                  {{ formatPrice(lot.price) }}
                </span>
                <template v-if="lot.additionalInfo?.ptn !== undefined">
                  <span class="lot-ptn">-{{ lot.additionalInfo.ptn }}</span>
                </template>
              </span>
            </template>
          </div>
        </template>
      </Column>

      <Column field="historySalePrices" header="График продаж">
        <template #body="{ data }">
          <div class="history-chart-container">
            <small>{{ data.historySalePrices?.length || 0 }} продаж</small>
            <HistoryChart
              :historySalePrices="data.historySalePrices || []"
              :sale-price="data.averagePrice"
            />
          </div>
        </template>
      </Column>

      <Column header="Действия">
        <template #body="{ data }">
          <div class="actions">
            <Button
              icon="pi pi-pencil"
              class="p-button-rounded p-button-text p-button-info"
              @click="editArtefact(data)"
              v-tooltip="'Редактировать'"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-rounded p-button-text p-button-danger"
              @click="confirmDelete(data)"
              v-tooltip="'Удалить'"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Диалог добавления/редактирования -->
    <Dialog
      v-model:visible="showAddDialog"
      :header="editingArtefact ? 'Редактировать артефакт' : 'Добавить артефакт'"
      :modal="true"
      :style="{ width: '900px' }"
    >
      <form @submit.prevent="saveArtefact">
        <div class="form-group">
          <label for="artefactId">Артефакт</label>
          <Dropdown
            id="artefactId"
            v-model="form.artefactId"
            :options="artefactOptions"
            optionLabel="name"
            optionValue="id"
            placeholder="Выберите артефакт"
            required
            editable
          />
        </div>

        <div class="form-group">
          <label for="quality">Качество</label>
          <div class="quality-buttons">
            <Button
              v-for="quality in QUALITY_LIST"
              :key="quality.value"
              :label="quality.label"
              :style="{ backgroundColor: quality.color }"
              class="quality-btn"
              :class="{ active: form.quality === quality.value }"
              @click="form.quality = quality.value"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="sharpening">Заточка</label>
          <div class="sharpening-buttons">
            <Button
              v-for="sharpening in SHARPENING_LIST"
              :key="sharpening.value"
              :label="sharpening.label"
              :style="{
                backgroundColor: sharpening.color,
                borderColor: sharpening.color,
                color: 'black'
              }"
              class="sharpening-btn"
              :class="{ active: form.sharpening === sharpening.label }"
              @click="form.sharpening = sharpening.label"
            />
          </div>
        </div>

        <div class="form-group">
          <label for="buyBelowPrice">Цена покупки (ниже)</label>
          <InputNumber id="buyBelowPrice" v-model="form.buyBelowPrice" :min="0" />
        </div>

        <div class="form-group">
          <label for="sellAbovePrice">Цена продажи (выше)</label>
          <InputNumber id="sellAbovePrice" v-model="form.sellAbovePrice" :min="0" />
        </div>

        <div class="form-actions">
          <Button label="Отмена" icon="pi pi-times" class="p-button-text" @click="cancelForm" />
          <Button
            :label="editingArtefact ? 'Сохранить' : 'Добавить'"
            icon="pi pi-check"
            type="submit"
            :loading="saving"
          />
        </div>
      </form>
    </Dialog>

    <!-- Диалог подтверждения удаления -->
    <Dialog
      v-model:visible="showDeleteDialog"
      header="Подтверждение удаления"
      :modal="true"
      :style="{ width: '400px' }"
    >
      <p>Вы уверены, что хотите удалить этот артефакт из отслеживаемых?</p>
      <template #footer>
        <Button
          label="Отмена"
          icon="pi pi-times"
          class="p-button-text"
          @click="showDeleteDialog = false"
        />
        <Button
          label="Удалить"
          icon="pi pi-trash"
          class="p-button-danger"
          @click="deleteArtefact"
          :loading="deleting"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useWebSocketStore } from '@/store/websocket.store'
import { computed, onMounted, ref } from 'vue'
import { api } from '@/api'
import { SHARPENING_LIST, QUALITY_LIST } from '@/common/list'
import { listArt, getNameArtFromId } from '@/common/list'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import InputNumber from 'primevue/inputnumber'
import ProgressSpinner from 'primevue/progressspinner'
import { useToast } from 'primevue/usetoast'
import HistoryChart from '@/components/HistoryChart.vue'

interface ITrackedArtefacts {
  id: number
  artefactId: string
  quality: number
  sharpening: string
  buyBelowPrice?: number
  sellAbovePrice?: number
  isActive: boolean
}

interface Recommendation {
  artefactId: string
  quality: number
  sharpening: string
  currentPrice: number
  averagePrice: number
  buyBelowPrice?: number
  sellAbovePrice?: number
  recommendation: string
  recommendationSeverity: 'success' | 'warning' | 'info' | 'danger'
}

const qltMap: Record<number, string> = {
  0: '#E0E0E0',
  1: '#4CAF50',
  2: '#2196F3',
  3: '#9C27B0',
  4: '#F44336',
  5: '#FFD700'
}

const getColorQlt = (qlt: number | undefined) => qltMap[qlt ?? 0] ?? 'white'

const wsStore = useWebSocketStore()
const artefactAllMap = computed(() => wsStore.artefactAllMap)
const trackedArtefacts = ref<ITrackedArtefacts[]>([])

// Управление диалогами и формами
const toast = useToast()
const showAddDialog = ref(false)
const showDeleteDialog = ref(false)
const editingArtefact = ref(null)
const deletingArtefact = ref(null)
const saving = ref(false)
const deleting = ref(false)

// Опции для выпадающего списка артефактов
const artefactOptions = ref(listArt)

const form = ref({
  artefactId: '',
  quality: 0,
  sharpening: '0-4',
  buyBelowPrice: null,
  sellAbovePrice: null
})

const recommendations = computed(() => {
  if (!artefactAllMap.value) return []

  return trackedArtefacts.value
    .map(artefact => {
      const rows = artefactAllMap.value[artefact.artefactId]?.rows

      if (rows) {
        const map = rows.find(
          el => el.qlt === artefact.quality && el.ptn === getSharpeningLabel(artefact.sharpening)
        )

        if (map) {
          const currentPrice = Number(map.currentPrice) || 0
          const averagePrice = Number(map.averagePrice) || 0
          const currentElems = map.currentElems || []

          let recommendation = `Ничего не делать`
          let recommendationSeverity: 'success' | 'warning' | 'info' | 'danger' = 'info'
          let actionRequired = false

          if (artefact.buyBelowPrice === 0 && artefact.sellAbovePrice === 0) {
            if (currentElems.length === 0 || currentPrice >= averagePrice) {
              recommendation = 'Продавать по средней цене'
              recommendationSeverity = 'success'
              actionRequired = true
            }
          } else if (artefact.sellAbovePrice) {
            if (currentElems.length === 0 || artefact.sellAbovePrice < currentPrice) {
              recommendation = `Выставлять > ${formatPrice(
                artefact.sellAbovePrice
              )}, конкурентов ниже нет`
              recommendationSeverity = 'success'
              actionRequired = true
            }
          } else if (artefact.buyBelowPrice) {
            if (artefact.buyBelowPrice >= currentPrice) {
              recommendation = `Покупать ниже ${formatPrice(artefact.buyBelowPrice)}`
              recommendationSeverity = 'success'
              actionRequired = true
            }
          }

          return {
            id: artefact.id,
            artefactId: artefact.artefactId,
            quality: artefact.quality,
            sharpening: artefact.sharpening,
            currentPrice,
            averagePrice,
            buyBelowPrice: artefact.buyBelowPrice,
            sellAbovePrice: artefact.sellAbovePrice,
            recommendation,
            recommendationSeverity,
            actionRequired,
            totalLots: currentElems.length,
            historySalePrices: map.historySalePrices || [],
            currentElems: currentElems
          }
        }
      }

      return null
    })
    .filter(item => item !== null)
    .sort((a, b) => {
      // Сортируем по приоритету действий
      if (a.actionRequired && !b.actionRequired) return -1
      if (!a.actionRequired && b.actionRequired) return 1
      return 0
    })
})

const getSharpeningLabel = sharpening => {
  const sharpeningItem = SHARPENING_LIST.find(s => s.value === sharpening)
  return sharpeningItem ? sharpeningItem.label : `${sharpening}`
}

const getArtefactName = (artefactId: string) => {
  const artefact = listArt.find(a => a.id === artefactId)
  return artefact ? artefact.name : artefactId
}

const formatPrice = (price: number | undefined | null) => {
  if (price === undefined || price === null) return '-'
  return price.toLocaleString('ru-RU')
}

const getPriceClass = (
  currentPrice: number,
  targetPrice: number | undefined | null,
  type: 'buy' | 'sell'
) => {
  if (!targetPrice) return ''

  const isGoodDeal = type === 'buy' ? currentPrice <= targetPrice : currentPrice >= targetPrice

  return isGoodDeal ? 'good-deal' : 'normal-price'
}

const shouldBuyLot = (lot: any, recommendation: any) => {
  if (recommendation.buyBelowPrice && lot.price <= recommendation.buyBelowPrice) {
    return true
  }

  return false
}

const copyToClipboard = (text: string, type: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.add({
        severity: 'info',
        summary: 'Скопировано',
        detail: text,
        life: 3000
      })
    })
    .catch(err => {
      console.error('Ошибка копирования:', err)
    })
}

const loadTrackedArtefacts = async () => {
  try {
    const response = await api.get('/tracked-artefacts')
    trackedArtefacts.value = response.data
  } catch (error) {
    console.log(error)
  }
}

const saveArtefact = async () => {
  saving.value = true
  try {
    if (editingArtefact.value) {
      await api.patch(`/tracked-artefacts/${editingArtefact.value.id}`, form.value)
      toast.add({
        severity: 'success',
        summary: 'Успех',
        detail: 'Артефакт обновлен',
        life: 3000
      })
    } else {
      await api.post('/tracked-artefacts', form.value)
      toast.add({
        severity: 'success',
        summary: 'Успех',
        detail: 'Артефакт добавлен',
        life: 3000
      })
    }

    cancelForm()
    await loadTrackedArtefacts()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.message || 'Не удалось сохранить артефакт',
      life: 3000
    })
  } finally {
    saving.value = false
  }
}

const editArtefact = artefact => {
  editingArtefact.value = artefact
  form.value = {
    artefactId: artefact.artefactId,
    quality: artefact.quality,
    sharpening: getSharpeningLabel(artefact.sharpening),
    buyBelowPrice: artefact.buyBelowPrice,
    sellAbovePrice: artefact.sellAbovePrice
  }
  showAddDialog.value = true
}

const confirmDelete = artefact => {
  deletingArtefact.value = artefact
  showDeleteDialog.value = true
}

const deleteArtefact = async () => {
  deleting.value = true
  try {
    await api.delete(`/tracked-artefacts/${deletingArtefact.value.id}`)
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Артефакт удален',
      life: 3000
    })
    showDeleteDialog.value = false
    deletingArtefact.value = null
    await loadTrackedArtefacts()
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось удалить артефакт',
      life: 3000
    })
  } finally {
    deleting.value = false
  }
}

const cancelForm = () => {
  showAddDialog.value = false
  editingArtefact.value = null
  form.value = {
    artefactId: '',
    quality: 0,
    sharpening: '0-4',
    buyBelowPrice: null,
    sellAbovePrice: null
  }
}

const openAddDialog = () => {
  cancelForm() // Сбрасываем форму
  showAddDialog.value = true // Открываем диалог
}

onMounted(() => {
  loadTrackedArtefacts()
})
</script>

<style lang="scss" scoped>
.track-dashboard {
  padding: 20px;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  h2 {
    margin-bottom: 20px;
    color: #fff;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 50px;
    color: #fff;

    p {
      margin-top: 20px;
      font-size: 16px;
    }
  }
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  color: #fff;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 2rem;
}

.quality-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.quality-btn {
  min-width: 80px;
  height: 45px;
  padding: 0 1rem;
  border: 2px solid;
  color: black !important;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-radius: 6px;
}

.quality-btn:hover {
  opacity: 0.8;
}

.quality-btn.active {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5);
}

.sharpening-buttons {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.sharpening-btn {
  min-width: 80px;
  height: 45px;
  padding: 0 1rem;
  border: 2px solid;
  color: black !important;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  border-radius: 6px;
}

.sharpening-btn:hover {
  opacity: 0.8;
}

.sharpening-btn.active {
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.5);
}

.history-chart-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;

  small {
    color: #ccc;
    font-size: 0.8em;
  }
}

.lots-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 300px;
}

.lot-item {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
  margin-right: 0;
  transition: all 0.2s ease;
}

.lot-to-buy {
  background-color: rgba(76, 175, 80, 0.2);
  border: 1px solid #4caf50;
  color: #4caf50;
  font-weight: 600;
}

.lot-to-buy:hover {
  background-color: rgba(76, 175, 80, 0.3);
}

.lot-normal {
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid #555;
  color: #ccc;
}

.lot-normal:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

.lot-price {
  font-weight: 600;
}

.clickable-lot-price {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 2px;
  padding: 1px 3px;
}

.clickable-lot-price:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.lot-ptn {
  opacity: 0.8;
  font-size: 0.9em;
}

.lot-separator {
  margin: 0 4px;
  opacity: 0.5;
}

.lot-info {
  margin-top: 5px;
  font-size: 0.8em;

  small {
    display: block;
    margin-top: 2px;

    &.below {
      color: #4caf50;
    }

    &.above {
      color: #f44336;
    }
  }
}

.good-deal {
  color: #4caf50;
  font-weight: bold;
}

.normal-price {
  color: #fff;
}

.clickable-price {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  padding: 2px 4px;
}

.clickable-price:hover {
  background-color: rgba(255, 255, 255, 0.1);
}
</style>
